import { useAuthContext } from "@/context/AuthContext";
import { useProgramContext } from "@/context/ProgramContext";
import { useEffect, useState } from "react";
import { Program } from "@t3/shared";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Container,
} from "@mui/material";
import { DateTime } from "luxon";

interface Participant {
  id: string;
  level: number;
  progress?: number;
  group: {
    level: {
      name: string;
      description: string;
    };
  };
}

interface ProgramWithParticipant extends Program {
  participant: Participant;
}

// Mock data for testing
const mockProgram: ProgramWithParticipant = {
  id: "mock-program-1",
  name: "Fizz of Fortune",
  regions: ["US"],
  features: ["cyc"],
  createdAt: DateTime.now(),
  modifiedAt: DateTime.now(),
  participant: {
    id: "mock-participant-1",
    level: 2,
    progress: 47,
    group: {
      level: {
        name: "Bubbling Star",
        description:
          "Your sales are fizzing up! You're on track to become a top performer.",
      },
    },
  },
};

const CircularProgressWithLabel = ({ value }: { value: number }) => {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        variant="determinate"
        value={value}
        size={120}
        thickness={4}
        sx={{
          color: (theme) => theme.palette.primary.main,
          "& .MuiCircularProgress-circle": {
            strokeLinecap: "round",
          },
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h4"
          component="div"
          color="text.secondary"
          sx={{ fontWeight: "bold" }}
        >
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

export const CYCProfile = () => {
  const { user: _user } = useAuthContext();
  const { program: _program, loading: programLoading } = useProgramContext();
  const [loading, setLoading] = useState(true);
  const [error, _setError] = useState<string | null>(null);

  useEffect(() => {
    if (!programLoading) {
      setLoading(false);
    }
  }, [programLoading]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <Typography color="error">{error}</Typography>
        </Box>
      </Container>
    );
  }

  // Use mock data for now
  const programWithParticipant = mockProgram;

  return (
    <Container maxWidth="lg">
      <Typography
        variant="h4"
        component="h4"
        sx={{
          mb: 3,
          mt: { xs: 10, md: 20 },
          fontWeight: "bold",
          textAlign: "center",
        }}
      >
        Choose Your Challenge
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ maxWidth: 400, width: "100%" }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
            }}
          >
            <Typography
              variant="h6"
              color="text.secondary"
              gutterBottom
              align="center"
            >
              {programWithParticipant.name}
            </Typography>
            <CircularProgressWithLabel
              value={programWithParticipant.participant.progress || 0}
            />
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {programWithParticipant.participant.group.level.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {programWithParticipant.participant.group.level.description}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "100%",
                mt: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="body2" color="text.secondary" align="center">
                Keep pedaling! You're making great progress towards your goal.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};
